/**
 * @file Pacientes (Patients) — restyled to match design page-pacientes.jsx.
 *
 * Layout:
 *  - pg-head + pg-stats + pg-toolbar
 *  - pac-with-rail: pg-table-card with pac-grid rows grouped by letter,
 *    plus pac-azrail vertical jump-to-letter nav
 *
 * Uses design CSS classes from src/client/styles/cd.css.
 */
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePatients } from "../hooks/usePatients";
import NewPatientDialog from "../components/NewPatientDialog";

const SVG = { fill: "none", stroke: "currentColor", strokeLinecap: "round", strokeLinejoin: "round" };

const I = {
  Search: () => (<svg width="14" height="14" viewBox="0 0 24 24" {...SVG} strokeWidth="1.7"><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.35-4.35"/></svg>),
  Plus: () => (<svg width="14" height="14" viewBox="0 0 24 24" {...SVG} strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>),
  Filter: () => (<svg width="14" height="14" viewBox="0 0 24 24" {...SVG} strokeWidth="1.7"><path d="M22 3H2l8 9.5V21l4-2v-6.5L22 3z"/></svg>),
  Users: () => (<svg width="44" height="44" viewBox="0 0 24 24" {...SVG} strokeWidth="1.4"><path d="M16 19v-1a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v1"/><circle cx="9" cy="7" r="3.5"/></svg>),
  ChevR: () => (<svg width="14" height="14" viewBox="0 0 24 24" {...SVG} strokeWidth="1.8"><path d="M9 6l6 6-6 6"/></svg>),
};

const ALPHABET = "ABCDEFGHIJKLMNÑOPQRSTUVWXYZ".split("");
const AVATAR_PALETTE = ["a1", "a2", "a3", "a4", "a5", "a6"];

const initials = (name) =>
  (name || "").split(" ").filter(Boolean).map((s) => s[0]).slice(0, 2).join("").toUpperCase() || "?";

const formatDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" }) : "—";

export default function Patients() {
  const navigate = useNavigate();
  const { patients, isLoading, refresh } = usePatients();
  const [search, setSearch] = useState("");
  const [newOpen, setNewOpen] = useState(false);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return patients;
    return patients.filter((p) => {
      const name = `${p.firstName || ""} ${p.lastName || ""}`.toLowerCase();
      const email = (p.email || "").toLowerCase();
      const phone = (p.phone || "").toLowerCase();
      return name.includes(q) || email.includes(q) || phone.includes(q);
    });
  }, [patients, search]);

  const groups = useMemo(() => {
    const out = {};
    filtered.forEach((p) => {
      const name = `${p.firstName || ""} ${p.lastName || ""}`.trim();
      const letter = (name[0] || "?").toUpperCase();
      (out[letter] ||= []).push(p);
    });
    Object.keys(out).forEach((k) => {
      out[k].sort((a, b) => {
        const an = `${a.firstName || ""} ${a.lastName || ""}`;
        const bn = `${b.firstName || ""} ${b.lastName || ""}`;
        return an.localeCompare(bn, "es");
      });
    });
    return out;
  }, [filtered]);

  const letters = Object.keys(groups).sort((a, b) => a.localeCompare(b, "es"));

  const scrollToLetter = (letter) => {
    const el = document.getElementById("pac-letter-" + letter);
    if (el && el.scrollIntoView) el.scrollIntoView({ block: "start", behavior: "smooth" });
  };

  return (
    <>
      <div className="pg-head">
          <div>
            <div className="cd-eyebrow">Directorio de pacientes</div>
            <h1 className="pg-title">Mis <em>pacientes</em></h1>
            <p className="pg-sub">
              {patients.length} {patients.length === 1 ? "paciente" : "pacientes"} registrados · busca y gestiona historiales.
            </p>
          </div>
          <div className="pg-actions">
            <button type="button" className="pg-btn cta" onClick={() => setNewOpen(true)}>
              <I.Plus /> Nuevo paciente
            </button>
          </div>
        </div>

        <div className="pg-stats">
          <div className="pg-stat">
            <div className="l">Total pacientes</div>
            <div className="v">{patients.length}</div>
            <div className="d">Acumulado</div>
          </div>
          <div className="pg-stat">
            <div className="l">Activos (90 días)</div>
            <div className="v">{patients.filter((p) => p.lastAppointmentAt && new Date(p.lastAppointmentAt).getTime() > Date.now() - 90 * 86400000).length}</div>
            <div className="d">Última cita reciente</div>
          </div>
          <div className="pg-stat">
            <div className="l">Letras</div>
            <div className="v">{letters.length}</div>
            <div className="d">Iniciales únicas</div>
          </div>
          <div className="pg-stat">
            <div className="l">Búsqueda</div>
            <div className="v">{filtered.length}</div>
            <div className="d">Resultados visibles</div>
          </div>
        </div>

        <div className="pg-toolbar">
          <div className="pg-search">
            <I.Search />
            <input
              placeholder="Buscar por nombre, correo o teléfono…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="pg-toolbar-spacer" />
        </div>

        {isLoading ? (
          <div className="pg-empty" style={{ padding: 40 }}>Cargando…</div>
        ) : filtered.length === 0 ? (
          <div className="pg-empty">
            <div className="glyph"><I.Users /></div>
            <h4>{patients.length === 0 ? "Aún no hay pacientes" : "Sin resultados"}</h4>
            <p>{patients.length === 0 ? "Tus pacientes aparecerán aquí después de su primera cita." : "Cambia la búsqueda para ver más."}</p>
          </div>
        ) : (
          <div className="pac-with-rail">
            <div className="pg-table-card">
              <div className="pg-table-head pac-grid">
                <div>Paciente</div>
                <div className="col-email">Correo</div>
                <div className="col-last">Última visita</div>
                <div className="col-visits">Citas</div>
                <div className="col-arrow"></div>
              </div>
              {letters.map((letter, lIdx) => (
                <React.Fragment key={letter}>
                  <div id={"pac-letter-" + letter} className="pac-letter-divider">{letter}</div>
                  {groups[letter].map((p, idx) => {
                    const fullName = `${p.firstName || ""} ${p.lastName || ""}`.trim();
                    return (
                      <div
                        key={p.id}
                        className="pg-table-row pac-grid"
                        onClick={() => navigate(`/paciente/${p.id}`)}
                      >
                        <div className="cita-patient">
                          <div className={"cd-avatar " + AVATAR_PALETTE[(lIdx + idx) % 6]}>
                            {initials(fullName)}
                          </div>
                          <div>
                            <div className="n">{fullName || "Paciente"}</div>
                            <div className="a">{p.phone || ""}</div>
                          </div>
                        </div>
                        <div className="col-email" style={{ fontSize: 12.5, color: "var(--ink-3)" }}>
                          {p.email || ""}
                        </div>
                        <div className="col-last" style={{ fontFamily: "var(--mono)", fontSize: 12, color: "var(--ink-2)" }}>
                          {formatDate(p.lastAppointmentAt)}
                        </div>
                        <div className="col-visits" style={{ fontWeight: 600, fontSize: 13, color: "var(--ink)" }}>
                          {p.appointmentCount ?? 0}
                        </div>
                        <div className="row-arrow col-arrow"><I.ChevR /></div>
                      </div>
                    );
                  })}
                </React.Fragment>
              ))}
            </div>
            <nav className="pac-azrail" aria-label="Saltar por letra">
              {ALPHABET.map((l) => (
                <a
                  key={l}
                  className={letters.includes(l) ? "" : "dim"}
                  onClick={() => letters.includes(l) && scrollToLetter(l)}
                  style={{ cursor: letters.includes(l) ? "pointer" : "default" }}
                >
                  {l}
                </a>
              ))}
            </nav>
          </div>
        )}

      <NewPatientDialog
        open={newOpen}
        onClose={() => setNewOpen(false)}
        onCreated={(record, { andSchedule }) => {
          refresh?.();
          if (andSchedule) navigate(`/nueva-cita?patientRecordId=${record.id}`);
          else navigate(`/paciente/${record.id}`);
        }}
      />
    </>
  );
}
